import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { setAuthRequiredHandler, setTokenGetter } from '@/lib/apiClient'
import { useProfileSync } from '@/hooks/useProfileSync'
import { ThreePanelLayout } from '@/components/layout/ThreePanelLayout'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { LoginPage } from '@/components/auth/LoginPage'
import { SignUpPage } from '@/components/auth/SignUpPage'
import { CreateAccountPage } from '@/components/auth/CreateAccountPage'
import { TermsPage, PrivacyPolicyPage, CoppaNoticePage } from '@/pages/legal/LegalPage'
import { ModuleRouter } from '@/modules/ModuleRouter'
import { PersonalizationComponentsTest } from '@/pages/PersonalizationComponentsTest'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// Admin diagnostic page — lazy so the games-debugger bundle never ships in
// the common chunk for regular users.
const AdminGamesPage = lazy(() =>
  import('@/pages/admin/AdminGamesPage').then((m) => ({ default: m.AdminGamesPage })),
)
const AdminGeneratePage = lazy(() =>
  import('@/pages/admin/AdminGeneratePage').then((m) => ({ default: m.AdminGeneratePage })),
)



// ---------------------------------------------------------------------------
// TokenBridge + ProfileSync
// ---------------------------------------------------------------------------
// Rendered inside Auth0Provider. Once the user is authenticated:
// 1. Registers getAccessTokenSilently with the Axios interceptor
// 2. Syncs the user profile to our backend and stores it in Zustand

function TokenBridge() {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0()

  useEffect(function connectAuth0TokenBridge() {
    if (!isAuthenticated) {
      setTokenGetter(null)
      setAuthRequiredHandler(null)
      return
    }

    setTokenGetter(() => getAccessTokenSilently())
    setAuthRequiredHandler(() =>
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
      }),
    )

    return function disconnectAuth0TokenBridge() {
      setTokenGetter(null)
      setAuthRequiredHandler(null)
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect])

  // Sync profile to backend after Auth0 authentication
  useProfileSync()

  return null
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

function App() {
  return (
    // Outermost boundary — catches crashes anywhere below, including inside
    // Auth0Provider/TokenBridge, not just inside the routed app. See
    // components/ErrorBoundary.tsx for why: before this, any render crash
    // white-screened the whole SPA with no recovery.
    <ErrorBoundary>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        cacheLocation="localstorage"
        useRefreshTokens
        useRefreshTokensFallback
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access',
        }}
      >
        <TokenBridge />
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              {/* Public auth routes */}
              <Route path="/auth/login" element={<LoginPage />} />
              {/* Invite-gated sign-up is now the front door (HOP-32). The old
                  Auth0 hosted register funnels into it so every signup starts
                  at the invite gate. */}
              <Route path="/auth/signup" element={<SignUpPage />} />
              <Route path="/auth/register" element={<Navigate to="/auth/signup" replace />} />
              {/* Create Account (name + email) → passwordless code (HOP-34/38/39). */}
              <Route path="/auth/create-account" element={<CreateAccountPage />} />
              {/* HOP-135 (P0, COPPA): Terms/Privacy/COPPA — public (no
                  ProtectedRoute) so a parent can read them before creating an
                  account, and so the parental-consent gate + CreateAccountPage
                  links resolve to something real. Real copy as of 2026-08-11
                  (pending counsel review) — see pages/legal/LegalPage.tsx. */}
              <Route path="/legal/terms" element={<TermsPage />} />
              <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/legal/coppa" element={<CoppaNoticePage />} />
              {/* Test routes (dev only) — actually gated: unreachable when the
                  app is built for production, not just a comment saying so. */}
              {import.meta.env.DEV && (
                <Route path="/test/personalization" element={<PersonalizationComponentsTest />} />
              )}
              {/* Admin diagnostic (role-gated, not dev-gated — must work on
                  staging/prod builds). No nav entry anywhere; admins type the
                  URL. Client gate is cosmetic; backend requireAdmin is real. */}
              <Route
                path="/admin/games"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <Suspense fallback={null}>
                        <AdminGamesPage />
                      </Suspense>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/generate"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <Suspense fallback={null}>
                        <AdminGeneratePage />
                      </Suspense>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              {/* Protected app routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <ThreePanelLayout>
                      <ModuleRouter />
                    </ThreePanelLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </Auth0Provider>
    </ErrorBoundary>
  )
}

export default App
